import React from 'react';
import { CitedSource } from '../types';

const URL_RE = /(https?:\/\/[^\s)]+)/g;
const BOLD_RE = /\*\*(.+?)\*\*/g;
const CITE_RE = /\[(\d+)\]/g;

interface Props {
  content: string;
  sources?: CitedSource[];
}

function parseInline(text: string, sources: CitedSource[]): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    URL_RE.lastIndex = 0;
    BOLD_RE.lastIndex = 0;
    CITE_RE.lastIndex = 0;

    const urlMatch = URL_RE.exec(remaining);
    const boldMatch = BOLD_RE.exec(remaining);
    const citeMatch = CITE_RE.exec(remaining);

    const candidates = [urlMatch, boldMatch, citeMatch]
      .filter(Boolean)
      .sort((a, b) => (a!.index ?? Infinity) - (b!.index ?? Infinity));

    if (candidates.length === 0) {
      parts.push(remaining);
      break;
    }

    const first = candidates[0]!;
    if (first.index > 0) {
      parts.push(remaining.slice(0, first.index));
    }

    if (first === urlMatch) {
      parts.push(
        <a
          key={key++}
          href={first[0]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline hover:text-blue-800 break-all"
        >
          {first[0]}
        </a>
      );
    } else if (first === boldMatch) {
      parts.push(<strong key={key++}>{first[1]}</strong>);
    } else if (first === citeMatch) {
      const num = parseInt(first[1], 10);
      const src = sources[num - 1];
      const url = src?.source_url;
      parts.push(
        url ? (
          <a
            key={key++}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 font-bold text-[9px] align-super hover:underline"
          >
            [{num}]
          </a>
        ) : (
          <sup key={key++} className="text-blue-500 font-bold text-[9px]">
            [{num}]
          </sup>
        )
      );
    }

    remaining = remaining.slice(first.index + first[0].length);
  }

  return parts;
}

export const MarkdownText: React.FC<Props> = ({ content, sources = [] }) => {
  const paragraphs = content.split(/\n\n+/);
  return (
    <div className="space-y-2">
      {paragraphs.map((para, pi) => {
        const lines = para.split('\n');
        return (
          <p key={pi}>
            {lines.map((line, li) => (
              <React.Fragment key={li}>
                {parseInline(line, sources)}
                {li < lines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
};
